import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Paperclip, ChevronRight, Search, Loader, CheckCheck, Trash2 } from 'lucide-react';
import { useLanguage } from '../i18n';
import { API_BASE, apiFetch } from '../config';

const PAGE_SIZE = 20;

// Swipeable email card (mobil uchun)
const SwipeableEmail = ({ email, onSelect, getInitial, getInitialColor, t }) => {
  const [offsetX, setOffsetX] = useState(0);
  const startXRef = useRef(null);
  const swipingRef = useRef(false);

  const handleTouchStart = (e) => { startXRef.current = e.touches[0].clientX; };
  const handleTouchMove = (e) => {
    if (startXRef.current === null) return;
    swipingRef.current = true;
    const diff = e.touches[0].clientX - startXRef.current;
    setOffsetX(Math.max(-80, Math.min(80, diff)));
  };
  const handleTouchEnd = async () => {
    if (offsetX > 50) {
      try { await apiFetch(API_BASE + '/api/emails/' + email.id + '/read', { method: 'PATCH' }); email.is_read = true; } catch (e) {}
    } else if (offsetX < -50) {
      try { await apiFetch(API_BASE + '/api/emails/' + email.id, { method: 'DELETE' }); } catch (e) {}
    }
    setOffsetX(0);
    startXRef.current = null;
    setTimeout(() => { swipingRef.current = false; }, 100);
  };

  return (
    <div className="relative overflow-hidden rounded-2xl">
      <div className="absolute inset-0 flex">
        <div className={'flex-1 flex items-center pl-4 transition-opacity ' + (offsetX > 30 ? 'bg-emerald-500/20 opacity-100' : 'opacity-0')}><CheckCheck size={18} className="text-emerald-400" /></div>
        <div className={'flex-1 flex items-center justify-end pr-4 transition-opacity ' + (offsetX < -30 ? 'bg-red-500/20 opacity-100' : 'opacity-0')}><Trash2 size={18} className="text-red-400" /></div>
      </div>
      <div
        onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
        onClick={() => !swipingRef.current && onSelect(email)}
        style={{ transform: 'translateX(' + offsetX + 'px)', transition: swipingRef.current ? 'none' : 'transform 0.3s' }}
        className={'glass-card-hover p-4 cursor-pointer' + (!email.is_read ? ' border-l-2 border-l-blue-500' : '')}
      >
        <div className="flex items-start gap-3">
          <div className={'w-10 h-10 rounded-xl bg-gradient-to-br ' + getInitialColor(email.sender) + ' flex items-center justify-center flex-shrink-0 text-white font-bold text-sm shadow-lg'}>{getInitial(email.sender)}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className={'text-xs font-mono truncate ' + (!email.is_read ? 'text-blue-400 font-semibold' : 'text-white/50')}>{email.sender}</span>
              <span className="text-[10px] text-white/30 flex-shrink-0">{email.date}</span>
            </div>
            <h3 className={'text-sm mt-1 truncate ' + (!email.is_read ? 'font-semibold text-white/90' : 'font-normal text-white/60')}>{email.subject}</h3>
            {(email.body_preview || email.body) && (
              <p className="text-[11px] text-white/35 mt-1 line-clamp-2 leading-relaxed">{(email.body_preview || (email.body || '').slice(0, 120)).replace(/\n/g, ' ')}</p>
            )}
            <div className="flex items-center gap-2 mt-2">
              {!email.is_read && <span className="bg-blue-500/20 text-blue-300 text-[10px] px-2 py-0.5 rounded-full font-medium">{t('emails_new')}</span>}
              {email.has_attachments && <span className="flex items-center gap-1 bg-white/5 text-white/40 text-[10px] px-2 py-0.5 rounded-full"><Paperclip size={10} />{t('emails_attachment')}</span>}
            </div>
          </div>
          <ChevronRight size={16} className="text-white/20 flex-shrink-0 mt-3" />
        </div>
      </div>
    </div>
  );
};

const EmailList = ({ emails, onSelect, onRefresh }) => {
  const { t } = useLanguage();
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  const listRef = useRef(null);

  // Debounced server-side search
  useEffect(() => {
    if (!search.trim()) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await apiFetch(
          API_BASE + '/api/emails?search=' + encodeURIComponent(search.trim()) + '&limit=50'
        );
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.emails || data);
        }
      } catch (err) {
        console.error('Qidiruv xatosi:', err);
      }
      setSearching(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Infinite scroll
  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 200 && !loadingMore) {
      setLoadingMore(true);
      setTimeout(() => {
        setDisplayCount(prev => prev + PAGE_SIZE);
        setLoadingMore(false);
      }, 300);
    }
  }, [loadingMore]);

  const getInitial = (sender) => {
    if (!sender) return '?';
    const name = sender.split('@')[0];
    return name.charAt(0).toUpperCase();
  };

  const getInitialColor = (sender) => {
    const colors = [
      'from-blue-500 to-blue-600',
      'from-cyan-500 to-cyan-600',
      'from-emerald-500 to-emerald-600',
      'from-violet-500 to-violet-600',
      'from-amber-500 to-amber-600',
      'from-rose-500 to-rose-600',
    ];
    let hash = 0;
    for (let i = 0; i < (sender || '').length; i++) {
      hash = sender.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const displayEmails = searchResults !== null ? searchResults : emails;
  const visibleEmails = displayEmails.slice(0, displayCount);
  const hasMore = displayCount < displayEmails.length;

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setDisplayCount(PAGE_SIZE);
  };

  const clearSearch = () => {
    setSearch('');
    setSearchResults(null);
    setDisplayCount(PAGE_SIZE);
  };

  return (
    <div className="screen-enter pb-24">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-white/90">{t('emails_title')}</h2>
        <span className="text-xs text-white/30 font-medium">{displayEmails.length} {t('emails_count')}</span>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30">
          {searching ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
        </span>
        <input
          type="text"
          value={search}
          onChange={handleSearch}
          placeholder="Qidirish (kimdan, mavzu, matn)..."
          className="w-full pl-9 pr-8 py-2 text-xs"
        />
        {search && (
          <button onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 text-xs">
            &#10005;
          </button>
        )}
      </div>

      {/* Email list with infinite scroll */}
      <div
        ref={listRef}
        onScroll={handleScroll}
        className="space-y-3 max-h-[calc(100vh-220px)] overflow-y-auto pr-1"
      >
        {visibleEmails.map((email) => (
          <SwipeableEmail key={email.id} email={email} onSelect={onSelect} getInitial={getInitial} getInitialColor={getInitialColor} t={t} />
        ))}

        {/* Loading more */}
        {loadingMore && (
          <div className="flex justify-center py-4">
            <Loader size={18} className="text-blue-400 animate-spin" />
          </div>
        )}

        {/* Has more indicator */}
        {hasMore && !loadingMore && (
          <div className="text-center py-3">
            <span className="text-[10px] text-white/30">Pastga suring — yana {displayEmails.length - displayCount} ta xat</span>
          </div>
        )}

        {displayEmails.length === 0 && (
          <div className="glass-card p-8 text-center">
            <p className="text-white/30 text-sm">{search ? 'Natija topilmadi' : t('emails_empty')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailList;
